import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";

export type PortalSelectOption = {
  value: string;
  label: string;
};

type PortalSelectBaseProps = {
  options: PortalSelectOption[];
  ariaLabel: string;
  disabled?: boolean;
  highlightActive?: boolean;
  openOnHover?: boolean;
  clearOnFocus?: boolean;
  virtualized?: boolean;
  className?: string;
};

type PortalSelectProps = PortalSelectBaseProps & {
  value: string;
  onChange: (value: string) => void;
  multiple?: false;
} | PortalSelectBaseProps & {
  value: string[];
  onChange: (value: string[]) => void;
  multiple: true;
};

/** A themed, keyboard-accessible, searchable alternative to a native select. */
export default function PortalSelect({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  highlightActive = false,
  openOnHover = false,
  clearOnFocus = false,
  virtualized = false,
  className = "",
  multiple = false,
}: PortalSelectProps) {
  const [query, setQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  useLayoutEffect(() => {
    if (!virtualized || !inputRef.current) return;
    // Virtual rows are positioned out of flow, so intrinsic width cannot size the menu.
    const context = document.createElement("canvas").getContext("2d");
    if (!context) return;
    const font = getComputedStyle(inputRef.current);
    context.font = `600 ${font.fontSize || "12px"} ${font.fontFamily || "sans-serif"}`;
    const widest = options.reduce((width, option) => Math.max(width, context.measureText(option.label).width), 0);
    // Include the checkmark, gap, horizontal padding, border and scrollbar.
    setContentWidth(Math.ceil(widest) + 64);
  }, [options, virtualized]);
  const selectedValues = Array.isArray(value) ? value : [value];
  const selected = options.filter((option) => selectedValues.includes(option.value));
  const selectionLabel = multiple
    ? selected.length ? (selected.length === 1 ? selected[0].label : `${selected.length} selected`) : "All"
    : selected[0]?.label || "All";
  const matchingOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return normalizedQuery
      ? options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
      : options;
  }, [options, query]);
  const virtualValues = useMemo(() => matchingOptions.map((option) => option.value), [matchingOptions]);
  const labelsByValue = useMemo(() => new Map(options.map((option) => [option.value, option.label])), [options]);
  const useVirtualList = virtualized && !multiple && matchingOptions.length > 0;

  return (
    <Combobox virtual={useVirtualList ? { options: virtualValues } : null} multiple={multiple} value={value} onChange={(nextValue) => {
      if (nextValue === null) return;
      if (multiple) {
        (onChange as (next: string[]) => void)(nextValue as string[]);
        return;
      }
      (onChange as (next: string) => void)(nextValue as string);
    }} disabled={disabled} immediate onClose={() => { setQuery(""); setIsEditing(false); }}>
      {({ open }) => <div className={`relative min-w-0 ${className}`} onMouseEnter={() => {
        if (openOnHover && !open && !disabled) buttonRef.current?.click();
      }}>
        <ComboboxInput
          ref={inputRef}
          aria-label={ariaLabel}
          displayValue={() => clearOnFocus && isEditing ? query : selectionLabel}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => { if (clearOnFocus) { setQuery(""); setIsEditing(true); } }}
          title={selectionLabel}
          className={`h-full w-full min-w-0 bg-transparent pr-6 text-xs font-bold outline-none transition placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-slate-500 ${highlightActive && selectedValues.length ? "text-orange-600 dark:text-orange-400" : "text-slate-900 dark:text-slate-100"}`}
        />
        <ComboboxButton ref={buttonRef} aria-label={`Open ${ariaLabel}`} className="absolute inset-y-0 right-0 flex w-6 items-center justify-center text-slate-500 outline-none focus-visible:ring-2 focus-visible:ring-orange-400 dark:text-slate-400">
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </ComboboxButton>
        <ComboboxOptions
          portal
          anchor={{ to: "bottom start", gap: "4px", padding: "8px" }}
          style={{
            width: virtualized ? `min(max(var(--input-width), ${contentWidth}px), calc(100vw - 2rem))` : undefined,
            "--anchor-max-height": "16rem",
          } as CSSProperties}
          className="z-[100] max-h-64 min-w-[var(--input-width)] w-max max-w-[calc(100vw-2rem)] overflow-auto border border-slate-200 bg-white py-1 text-xs shadow-xl outline-none dark:border-white/10 dark:bg-slate-900"
        >
          {useVirtualList ? ({ option }: { option: string }) => (
            <ComboboxOption value={option} title={labelsByValue.get(option)} className="group flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-slate-700 select-none data-[focus]:bg-orange-50 data-[selected]:font-bold dark:text-slate-200 dark:data-[focus]:bg-orange-400/15">
              <Check className="h-3.5 w-3.5 shrink-0 text-orange-500 opacity-0 group-data-[selected]:opacity-100" aria-hidden="true" />
              <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">{labelsByValue.get(option)}</span>
            </ComboboxOption>
          ) : <>
          {multiple ? <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => (onChange as (next: string[]) => void)([])} className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left font-bold text-slate-700 hover:bg-orange-50 focus-visible:bg-orange-50 focus-visible:outline-none dark:text-slate-200 dark:hover:bg-orange-400/15 dark:focus-visible:bg-orange-400/15">
            <Check className={`h-3.5 w-3.5 shrink-0 text-orange-500 ${!selectedValues.length ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
            <span>All</span>
          </button> : null}
          {matchingOptions.map((option) => (
            <ComboboxOption key={option.value} value={option.value} className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-slate-700 select-none data-[focus]:bg-orange-50 data-[selected]:font-bold dark:text-slate-200 dark:data-[focus]:bg-orange-400/15">
              <Check className="h-3.5 w-3.5 shrink-0 text-orange-500 opacity-0 group-data-[selected]:opacity-100" aria-hidden="true" />
              <span className="whitespace-nowrap">{option.label}</span>
            </ComboboxOption>
          ))}
          {!matchingOptions.length ? <p className="px-3 py-2 text-slate-500 dark:text-slate-400">No matching options</p> : null}
          </>}
        </ComboboxOptions>
      </div>}
    </Combobox>
  );
}
